import {Component, ViewEncapsulation} from '@angular/core';
import {CommonConstants, CountryCodes, RoutesConstants, ImagePathConstants} from '@coreconstant';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {lastValueFrom} from 'rxjs';
import {ResponseHandlerService} from '../core/services/common-service/response-handler.service';
import {RequestParamModel} from '../core/models/request-param-model';
import {cannotContainSpace, noWhitespaceValidator} from '../core/services/common-service/common.service';
import {ActivatedRoute, RouterModule} from '@angular/router';
import {IMaskModule} from 'angular-imask';
import {NgSelectModule} from '@ng-select/ng-select';
import {CommonModule} from '@angular/common';
import {SeoService} from '../core/services/common-service/seo.service';
import {RecaptchaModule} from 'ng-recaptcha-2';
import {MetaDataConstants} from '../core/constants/meta-data-constants';
import {UserService} from '../core/services/api-service/user.service';
import {CountryService} from '../core/services/api-service/country.service';
import {VendorTypesService} from '../core/services/api-service/vendor-types.service';
import {RegionService} from '../core/services/api-service/region.service';
import {BusinessGoalService} from '../core/services/api-service/business-goal.service';
import {RecaptchaService} from '../core/services/api-service/recaptcha.service';

@Component({
    selector: 'app-sign-up',
    imports: [RouterModule, IMaskModule, NgSelectModule, CommonModule, ReactiveFormsModule, RecaptchaModule],
    templateUrl: './sign-up.component.html',
    styleUrl: './sign-up.component.scss',
    encapsulation: ViewEncapsulation.None
})
export class SignUpComponent {
    imagePathConstants = ImagePathConstants
    routesConstants = RoutesConstants
    commonConstants = CommonConstants
    countryCodes = CountryCodes
    requestParamModel!: RequestParamModel;
    signUpForm: FormGroup;
    currentStep = 0;
    fragment: string = this.commonConstants.BUSINESS_TYPE_VENUE
    countryList = []
    regionList = []
    serviceTypes = []
    businessGoals = []
    selectedBusinessTypes = []
    isNextOrSubmit: boolean = false
    selectedServiceType = this.commonConstants.BUSINESS_TYPE_VENUE
    isSubmitAndShowSuccess: boolean = false

    constructor(
        private _formBuilder: FormBuilder,
        private _activatedRoute: ActivatedRoute,
        private _userService: UserService,
        private _businessGoalService: BusinessGoalService,
        private _countryService: CountryService,
        private _vendorTypesService: VendorTypesService,
        private _regionService: RegionService,
        private _seoService: SeoService,
        private _recaptchaService: RecaptchaService,
        private _responseHandlerService: ResponseHandlerService,
    ) {
        this.setMetaTags()
        this.requestParamModel = new RequestParamModel();
    }

    /**
     * Sets the meta tags for the current page by creating a meta tag object and passes it to the SEO service for application.
     * @return {void} Does not return a value.
     */
    setMetaTags() {
        let metaTagObject = {
            title: MetaDataConstants.SIGN_UP.TITLE,
            description: MetaDataConstants.SIGN_UP.DESCRIPTION,
            ogImageUrl: MetaDataConstants.SIGN_UP.OGIMAGEURL,
            ogWebUrl: this.routesConstants.SIGN_UP_NAVIGATION,
        }
        this._seoService.setMetaTags(metaTagObject)
    }

    /**
     * Initializes the component and its dependent processes.
     */
    ngOnInit() {
        this._activatedRoute.fragment.subscribe(fragment => {
            if (fragment) {
                this.fragment = fragment;
                if (fragment == this.commonConstants.BUSINESS_TYPE_SERVICE)
                    this.selectedServiceType = fragment
            }
        });

        this.signUpForm = this._formBuilder.group({
            step1: this._formBuilder.group({
                businessType: [this.fragment, [Validators.required,]],
                name: ['', [Validators.required, noWhitespaceValidator]],
                countryId: [null, [Validators.required]],
                stateId: ['', Validators.required],
                regionId: [null, Validators.required],
                serviceType: [null, this.fragment == this.commonConstants.BUSINESS_TYPE_SERVICE ? [Validators.required] : []],
                yearsInBusiness: [null, Validators.required],
                annualBookingNumbers: [null, Validators.required],
                isGettingStarted: [false],
                websiteUrl: ['', [Validators.pattern(this.commonConstants.WEBSITE_REGEX)]],
                instagramUserName: ['', cannotContainSpace],
            }),
            step2: this._formBuilder.group({
                achieveReason: ['', Validators.required],
            }),
            step3: this._formBuilder.group({
                firstName: ['', [Validators.required, noWhitespaceValidator]],
                lastName: [''],
                emailAddress: ['', [Validators.required, Validators.pattern(this.commonConstants.EMAIL_REGEX)]],
                countryIdForMobile: [this.commonConstants.AUSTRALIA_COUNTRY_CODE, Validators.required],
                mobileNumber: ['', [Validators.required, Validators.pattern(this.commonConstants.MOBILE_REGEX)]],
                description: [''],
                recaptcha: ['', Validators.required],
            }),
        });

        this.getCountries()
        this.getBusinessGoals()

        if (this.fragment == this.commonConstants.BUSINESS_TYPE_SERVICE) {
            this.getVendorTypeList()
        }
    }

    /**
     * Handles the resolution of a CAPTCHA, verifying the user's response and updating the form based on the verification result.
     * @param {string} captchaResponse - The CAPTCHA response token generated after user completes the CAPTCHA challenge.
     */
    onCaptchaResolved(captchaResponse: string) {
        let input = {
            token: captchaResponse,
        }
        let verifyCaptchaResponse = this._recaptchaService.verifyCaptcha(input)
        lastValueFrom(verifyCaptchaResponse).then((response: any) => {
            if (response["status"] == true) {
                if (response["data"]["success"] == true) {
                    this.signUpForm.get('step3')?.patchValue({recaptcha: response["data"]});
                } else {
                    this.signUpForm.get('step3')?.patchValue({recaptcha: null});
                }

            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error);
        });
    }

    /**
     * Retrieves the current group from the sign-up form based on the current step.
     *
     * @return {Object} The group corresponding to the current step in the sign-up process.
     */
    get getCurrentGroup() {
        const steps = ['step1', 'step2', 'step3'];
        return this.signUpForm.get(steps[this.currentStep])
    }

    /**
     * Change validation based on the business type
     * @param event
     */
    businessTypeChange(event: any) {
        if (event.target.value == this.commonConstants.BUSINESS_TYPE_VENUE) {
            this.selectedServiceType = this.commonConstants.BUSINESS_TYPE_VENUE;
            this.getCurrentGroup.get('serviceType').clearValidators();
            this.getCurrentGroup.get('serviceType').updateValueAndValidity();

        } else if (event.target.value == this.commonConstants.BUSINESS_TYPE_SERVICE) {
            this.getVendorTypeList()
            this.selectedServiceType = this.commonConstants.BUSINESS_TYPE_SERVICE;
            this.getCurrentGroup.get('serviceType').setValidators([Validators.required]);
            this.getCurrentGroup.get('serviceType').updateValueAndValidity();
        }
    }

    /**
     * Adds or removes a business type ID from the selectedBusinessTypes array.
     * @param {number} businessTypeId - The ID of the business type to be added or removed.
     */
    addBusinessType(businessTypeId: number) {
        if (this.selectedBusinessTypes.includes(businessTypeId)) {
            this.selectedBusinessTypes = this.selectedBusinessTypes.filter(id => id != businessTypeId);
        } else {
            this.selectedBusinessTypes.push(businessTypeId);
        }
        this.getCurrentGroup.get('achieveReason').setValue(this.selectedBusinessTypes);
    }

    /**
     * Change validation based on the getting started field
     * @param event
     */
    isGettingStartedChange(event: any) {
        if (event.target.checked) {
            this.getCurrentGroup.get('yearsInBusiness').clearValidators();
            this.getCurrentGroup.get('annualBookingNumbers').clearValidators();
        } else {
            this.getCurrentGroup.get('yearsInBusiness').setValidators([Validators.required]);
            this.getCurrentGroup.get('annualBookingNumbers').setValidators([Validators.required]);
        }
        this.getCurrentGroup.get('yearsInBusiness').updateValueAndValidity();
        this.getCurrentGroup.get('annualBookingNumbers').updateValueAndValidity();
    }

    /**
     * Retrieves a list of countries by making a request to the country service.
     */
    getCountries() {
        const requestModel = new RequestParamModel();
        requestModel.addDefaultFilter('data', "all")
        delete requestModel.pageSize
        let getListing = this._countryService.getCountryList(requestModel)
        lastValueFrom(getListing).then((res: any) => {
            if (res["data"]) {
                this.countryList = res["data"]
            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error)
        });
    }

    /**
     * Handles the selection of a country within the sign-up form.
     */
    onSelectCountry() {
        let step1Values = this.signUpForm.controls['step1'] as FormGroup
        let selectedCountryId = step1Values.controls['countryId']?.value;
        step1Values.controls['stateId'].setValue(null);
        step1Values.controls['regionId'].setValue(null);

        this.requestParamModel.removeDefaultFilter('countryId');
        this.requestParamModel.addDefaultFilter('countryId', selectedCountryId);
        this.requestParamModel.addDefaultFilter('data', "all");
        delete this.requestParamModel.pageSize

        let getRegions = this._regionService.getRegionList(this.requestParamModel)
        lastValueFrom(getRegions).then((res: any) => {
            if (res["data"]) {
                this.regionList = res["data"]
            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error)
        });
    }

    /**
     * Handles the selection of a region within a form. Updates the associated state ID when a region is selected.
     * */
    onSelectRegion() {
        let step1Values = this.signUpForm.controls['step1'] as FormGroup
        let selectedRegionId = step1Values.controls['regionId']?.value;

        let selectedRegionData = this.regionList.find(region => region.id == selectedRegionId)
        if (selectedRegionData && selectedRegionData.stateId) {
            step1Values.controls['stateId'].setValue(selectedRegionData.stateId);
        }
    }

    /**
     * Fetches the list of vendor types from the vendor types service.
     */
    getVendorTypeList() {
        const requestModel = new RequestParamModel();
        requestModel.addDefaultFilter('data', "all")
        delete requestModel.pageSize
        let getVendorTypeList = this._vendorTypesService.getVendorTypeList(requestModel)
        lastValueFrom(getVendorTypeList).then((res: any) => {
            if (res["data"]) {
                this.serviceTypes = res["data"]
            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error)
        });
    }

    /**
     * Fetches the list of business goals from the business goal service.
     */
    getBusinessGoals() {
        let getBusinessGoals = this._businessGoalService.getBusinessGoals()
        lastValueFrom(getBusinessGoals).then((res: any) => {
            if (res["status"]) {
                this.businessGoals = res["data"]
            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error)
        });
    }

    /**
     * Advances to the next step in the process if the current group is valid.
     * If the current group is invalid, it marks all fields as touched.
     */
    nextStep() {
        this.isNextOrSubmit = true
        const currentGroup = this.getCurrentGroup;

        if (currentGroup.invalid) {
            currentGroup.markAllAsTouched();
        } else {
            this.currentStep++;
            this.signUpForm.get('step3')?.patchValue({recaptcha: null});
        }
    }

    /**
     * move back to previous sign up step.
     */
    prevStep() {
        this.currentStep--;
    }

    /**
     * Handles the form submission process by validating the form.
     */
    onSubmit() {
        this.isNextOrSubmit = true

        if (this.signUpForm.invalid) {
            this.signUpForm.markAllAsTouched();
            return false;
        }

        const step1Values = this.signUpForm.get('step1').value;
        const step2Values = this.signUpForm.get('step2').value;
        const step3Values = this.signUpForm.get('step3').value;

        const flattenedValues = {
            ...step1Values,
            ...step2Values,
            ...step3Values
        };

        let addUpdateRecord = this._userService.signUp(flattenedValues)
        lastValueFrom(addUpdateRecord).then((response: any) => {
            if (response["status"] == true) {
                this.isSubmitAndShowSuccess = true
            } else {
                this._responseHandlerService.showErrorMessageGeneral(response["message"] ?? this.commonConstants.SOMETHING_WENT_WRONG)
            }
        }, (error: any) => {
            this._responseHandlerService.handleAPIErrorResponse(error);
        });
    }
}
